/**
 * Pure predicates for deciding whether a captured frontend error is a hydration mismatch, and whether
 * that mismatch belongs to something that rewrote the document rather than to us.
 *
 * Dependency-free on purpose (no React, no query client, no inline script) so every rule here is unit
 * testable without a DOM harness. The stateful half — the mutation recorder that feeds
 * {@link wasOurTextReplaced}, and the single gate every report path asks — lives in
 * `./hydration-rewrite-monitor`.
 *
 * Carried over from the Worldcoo portals codebase, where the mechanism was measured against a
 * production build on iOS: the same page-translator wave produces the same #418s here, and a mismatch
 * we cannot attribute is a GitHub issue nobody can close.
 */

/**
 * React error numbers meaning "the server markup and the client's first render disagreed". 418 is the
 * mismatch itself (`args[]=text` or `args[]=HTML`); the others are the neighbouring hydration failures
 * React can report while recovering the same tree.
 *
 * The e2e harness matches a narrower set (418/423/425 — see `test-utils/e2e.ts`) because a spec asserts
 * on the mismatch it can reproduce. This set is deliberately wider: in production we are deciding
 * whether an arriving report is hydration-class at all, and a false negative there means the report
 * skips the attribution gate below and tickets a translator's doing as ours.
 */
const HYDRATION_ERROR_NUMBERS = new Set([418, 421, 422, 423, 425])

export function isHydrationError(message: string): boolean {
  const number = /minified react error #(\d+)/i.exec(message)?.[1]
  if (number) return HYDRATION_ERROR_NUMBERS.has(Number(number))
  return message.toLowerCase().includes('hydrat')
}

/**
 * The `<html lang>` the DOCUMENT ARRIVED with, captured when this module first evaluates — before
 * hydration, and before a page translator has had a reason to touch a page the visitor is still
 * waiting on. It is the reference {@link isDocumentTranslated} compares against.
 */
const initialDocumentLang = typeof document !== 'undefined' ? document.documentElement.lang : undefined

/**
 * Whether a page translator has mutated this document. Two signals:
 *
 * - Chrome/Edge stamp `translated-ltr` / `translated-rtl` on `<html>` when Google Translate runs.
 * - Any translator (Safari's, extensions) rewrites `<html lang>` to the TARGET language, so a `lang`
 *   that differs from the one the document arrived with means something rewrote the page after load.
 *
 * Why it exists: translators rewrite text nodes — including number and date punctuation, so a French
 * target turns `7,303.00` into `7 303,00` — and wrap them in `<font>` elements, which React's hydration
 * reads as text (`#418 args[]=text`) and structural (`args[]=HTML`) mismatches it then recovers from.
 * Every victim of that wave on the portals was a non-English browser auto-translating a page served in
 * another language; the same is true of a Spanish-default app read in a Portuguese browser.
 *
 * Known trade-off: the in-app language switcher rewrites `<html lang>` too, so after a manual switch
 * this returns true and a hydration-class error would be dropped. Hydration only happens at initial
 * load, before any switch is possible, so nothing real is lost — and a translated document makes any
 * later hydration-shaped report untrustworthy anyway.
 *
 * Parameters exist for tests only; callers use the zero-argument form.
 */
export function isDocumentTranslated(
  html: { className: string; lang: string } | undefined = typeof document !== 'undefined'
    ? document.documentElement
    : undefined,
  arrivedLang: string | undefined = initialDocumentLang,
): boolean {
  if (!html) return false
  if (/(?:^|\s)translated-(?:ltr|rtl)(?:\s|$)/.test(html.className)) return true
  return !!arrivedLang && !!html.lang && html.lang !== arrivedLang
}

/** One text node's value being swapped for another, as recorded by the hydration rewrite monitor. */
export interface TextReplacement {
  readonly before: string
  readonly after: string
}

/**
 * How much of a witness has to line up before a replacement counts as "this was our text". Long enough
 * that no unrelated node collides with it, short enough to survive the monitor truncating a long value.
 * When the DOM splits prose across text nodes, only the FIRST fragment still shares the witness's
 * prefix — later fragments simply never match, which fails open into a report.
 */
const MIN_WITNESS_MATCH = 24

/**
 * The DOM collapses whitespace runs and renders `&nbsp;` as U+00A0; compare past both.
 *
 * U+00A0 is matched through its `\u00a0` ESCAPE, never as the character itself. A literal non-breaking
 * space here is invisible in every diff and one formatter pass away from becoming an ordinary space —
 * which would leave a space-for-space no-op whose only effect is to make this comment a lie, and the
 * whitespace test would keep passing because JS `\s` matches U+00A0 too.
 */
function normalizeText(text: string): string {
  return text
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function sharesPrefix(a: string, b: string, length: number): boolean {
  if (a.length < length || b.length < length) return false
  for (let i = 0; i < length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/**
 * Whether something outside the app replaced text the app had rendered — a page translator, a content
 * blocker, an in-app-WebView injection.
 *
 * `witnesses` are strings the app can prove it rendered, harvested from the dehydrated TanStack Query
 * cache (see `renderedTextWitnesses`). `replacements` is the monitor's log of text-node value changes.
 * A replacement that took one of our own strings away and put something we cannot account for in its
 * place was a foreign agent; BOTH ends are checked, because the direction alone does not separate the
 * two events:
 *
 *     before = our text, after = not ours   ->  a foreign agent REPLACED it
 *     before = not ours, after = our text   ->  React RESTORED it (repairing a mismatch)
 *     before = our text, after = our text   ->  React swapped one of OUR renders for another
 *
 * That third line is the one worth spelling out. Witnesses come from the cache the SERVER rendered
 * from, so React repairing a genuine mismatch writes our-text -> our-text: the server's string out, the
 * client's string in. Testing `after` against only the witness that matched `before` classifies that as
 * foreign and SILENCES the regression — precisely the class of bug this whole path exists to keep
 * visible. Both ends are therefore matched against EVERY witness.
 *
 * Residual limit, accepted knowingly: if the client's value is not in the cache at all (nothing to
 * witness it), such a repair still reads as foreign. Narrowing further needs the monitor to say whether
 * a replacement predates `hydrateRoot` — the counts in the report's `rewriteCheck` line are there to
 * tell us whether that step is needed.
 *
 * Why it is a log rather than a comparison against the DOM: the DOM cannot answer this at any moment.
 * Sampling before `hydrateRoot` is too early, because hydration runs inside a `startTransition` and is
 * therefore concurrent — the rewrite lands mid-flight. Sampling when the error is reported is too late,
 * because React's recovery RESTORES the very text you would use as the witness. The event survives; the
 * state does not.
 *
 * Matched on a shared prefix, not equality or `includes`: the monitor caps recorded values, and prose
 * gets split across text nodes, so neither side is reliably whole.
 *
 * FAILS OPEN — no witnesses, no replacements, nothing long enough to match: false, i.e. "not
 * rewritten", i.e. keep reporting. A missed classification costs one noisy issue; a wrong one hides the
 * next real hydration regression.
 */
export function wasOurTextReplaced(replacements: readonly TextReplacement[], witnesses: readonly string[]): boolean {
  if (replacements.length === 0 || witnesses.length === 0) return false
  const normalizedWitnesses = witnesses.map(normalizeText).filter((text) => text.length >= MIN_WITNESS_MATCH)
  if (normalizedWitnesses.length === 0) return false
  const isOurs = (text: string) => normalizedWitnesses.some((witness) => sharesPrefix(text, witness, MIN_WITNESS_MATCH))
  return replacements.some((replacement) => {
    const before = normalizeText(replacement.before)
    if (before.length < MIN_WITNESS_MATCH) return false
    // Against EVERY witness, not just the one `before` matched: a value that is also ours means React
    // swapping the server's render for the client's, which is a real mismatch and must keep reporting.
    return isOurs(before) && !isOurs(normalizeText(replacement.after))
  })
}

/** The shortest string that can serve as a witness. Exported so the harvester applies the same floor. */
export const MIN_WITNESS_LENGTH = MIN_WITNESS_MATCH
